import React from 'react';
import {
  Globe,
  CalendarCheck,
  CreditCard,
  Users,
  RefreshCw,
  Code2,
  Palette,
  Cloud,
  ClipboardList,
  ShoppingCart,
  MessageCircle,
  GraduationCap,
  KanbanSquare,
  Briefcase,
  Building2,
  FolderCog,
} from 'lucide-react';
import useScrollReveal from '../hooks/useScrollReveal';
import './Services.css';

/* ── core services we offer ── */
const services = [
  {
    icon: <Globe />,
    image: '/services/Portfolio (5).png',
    title: 'Official Website Portfolio',
    desc: 'Professional company websites that showcase your brand, services, and work to build trust with clients.',
  },
  {
    icon: <CalendarCheck />,
    image: '/services/booking.png',
    title: 'Booking Systems',
    desc: 'Online reservation and appointment scheduling for services, rentals, and events.',
  },
  {
    icon: <CreditCard />,
    image: '/services/pos.png',
    title: 'POS',
    desc: 'Point-of-sale systems for seamless in-store transactions, inventory, and sales tracking.',
  },
  {
    icon: <Users />,
    image: '/services/crm.png',
    title: 'CRM Systems',
    desc: 'Customer relationship management tools to organize leads, track interactions, and grow sales.',
  },
  {
    icon: <RefreshCw />,
    image: '/services/enhance.png',
    title: 'Existing Website Enhancement',
    desc: 'Upgrades, redesigns, and performance improvements for your current website.',
  },
  {
    icon: <Code2 />,
    image: '/services/custom.png',
    title: 'Custom Website Application',
    desc: 'Bespoke web applications built to match your exact business workflow.',
  },
  {
    icon: <Palette />,
    image: '/services/ui.png',
    title: 'UI/UX Design',
    desc: 'Intuitive, engaging, and beautiful user interfaces that delight your customers.',
  },
  {
    icon: <Cloud />,
    image: '/services/saas.png',
    title: 'SaaS Applications',
    desc: 'Scalable software-as-a-service platforms with subscription and multi-tenant support.',
  },
  {
    icon: <ClipboardList />,
    image: '/services/ordering.png',
    title: 'Ordering System',
    desc: 'Digital ordering platforms for food, retail, and service-based businesses.',
  },
];

/* ── types of applications / industries we build for ── */
const industries = [
  {
    icon: <ShoppingCart />,
    image: '/services/e-commerce.png',
    title: 'E-commerce',
    desc: 'Online stores, marketplaces, and payment systems.',
    examples: ['Amazon', 'Shopify'],
  },
  {
    icon: <MessageCircle />,
    image: '/services/social.png',
    title: 'Social Networking & Communication',
    desc: 'Platforms for connecting, messaging, and sharing.',
    examples: ['Facebook', 'Discord', 'Slack'],
  },
  {
    icon: <GraduationCap />,
    image: '/services/elearning.png',
    title: 'Educational & E-learning',
    desc: 'Online courses, learning platforms, and study tools.',
    examples: ['Coursera', 'Khan Academy'],
  },
  {
    icon: <KanbanSquare />,
    image: '/services/productivity.png',
    title: 'Productivity & Collaboration',
    desc: 'Project management, task tracking, and team collaboration.',
    examples: ['Trello', 'Notion', 'Microsoft Teams'],
  },
  {
    icon: <Briefcase />,
    image: '/services/portfolio.png',
    title: 'Portfolio & Personal Branding',
    desc: 'Showcasing skills, resumes, and creative work.',
    examples: ['Behance', 'Personal portfolio sites'],
  },
  {
    icon: <Building2 />,
    image: '/services/business.png',
    title: 'Business & Enterprise Systems',
    desc: 'ERP, CRM, and HR management tools.',
    examples: ['Salesforce', 'SAP'],
  },
  {
    icon: <FolderCog />,
    image: '/services/utility.png',
    title: 'Utility & Tools',
    desc: 'File storage, cloud services, and productivity utilities.',
    examples: ['Google Drive', 'Dropbox'],
  },
];

export default function Services() {
  useScrollReveal();

  return (
    <>
      {/* ═══════════════════════════════════════════
          SERVICES
      ═══════════════════════════════════════════ */}
      <section className="services" id="services">
        <div className="container">
          <div className="section-label">TRUSTED SERVICES</div>
          <h2 className="section-title">Comprehensive Tech Solutions</h2>
          <p className="section-sub">
            We provide end-to-end digital services to help your business scale, secure its assets,
            and stand out in the market.
          </p>

          <div className="services-grid">
            {services.map((s, i) => (
              <div className="service-card reveal-card" key={s.title} style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="service-image-wrap">
                  <div className="service-image-clip">
                    <img src={s.image} alt={s.title} className="service-image" />
                  </div>
                  <div className="service-icon">{s.icon}</div>
                </div>
                <div className="service-body">
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
                <div className="card-corner"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          INDUSTRIES / TYPES WE BUILD
      ═══════════════════════════════════════════ */}
      <section className="industries" id="industries">
        <div className="container">
          <div className="section-label">WHAT WE BUILD</div>
          <h2 className="section-title">Industries & Application Types We Serve</h2>
          <p className="section-sub">
            From marketplaces to management systems, we build applications tailored to how your
            industry actually works.
          </p>

          <div className="industries-grid">
            {industries.map((ind, i) => (
              <div className="industry-card reveal-card" key={ind.title} style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="industry-image-wrap">
                  <div className="industry-image-clip">
                    <img src={ind.image} alt={ind.title} className="industry-image" />
                  </div>
                  <div className="industry-icon">{ind.icon}</div>
                </div>
                <div className="industry-body">
                  <h3>{ind.title}</h3>
                  <p>{ind.desc}</p>
                  <div className="industry-examples">
                    {ind.examples.map((ex) => (
                      <span className="industry-tag" key={ex}>{ex}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}